// vue
import { ref } from '@vue/composition-api';
import { useStore } from '@/store';

// hooks
import { useAxios } from '@/vue3/hooks/axiosHook';
import {
    GeoRecordCategory,
    GeoRecordCategorySummary,
} from '@/vue3/api/models/geoRecordCategoryModels';

export const useGeoRecordCategoryService = () => {
    // hooks
    const { apiUrl } = useStore().state;
    const axios = useAxios();

    // reactive
    const geoRecordCategories = ref<GeoRecordCategory[]>([]);

    // methods
    const getAllGeoRecordCategory = async (): Promise<GeoRecordCategory[]> => {
        const res = await axios.get(`${apiUrl}/geo-record-category`);
        geoRecordCategories.value = res.data;
        return res.data;
    };

    const getAllGeoRecordCategoryByOrg = async (
        org_id,
    ): Promise<GeoRecordCategory[]> => {
        const res = await axios.get(
            `${apiUrl}/geo-record-category/org/${org_id}`,
        );
        geoRecordCategories.value = res.data ? res.data : [];
        return res.data ? res.data : [];
    };

    const getAllGeoRecordCategoryAsTree = async (): Promise<any[]> => {
        const res = await axios.get(`${apiUrl}/geo-record-category/tree`);
        return res.data;
    };

    const getAllGeoRecordCategoryAsTreeByRole = async (): Promise<any[]> => {
        const res = await axios.get(`${apiUrl}/geo-record-category/role/tree`);
        return res.data;
    };

    const getAllGeoRecordCategoryAsTreeByOrg = async (
        organization_id: number,
    ): Promise<any[]> => {
        const res = await axios.get(
            `${apiUrl}/geo-record-category/tree/${organization_id}`,
        );
        return res.data;
    };

    const createGeoRecordCategory = async (
        category: GeoRecordCategory,
    ): Promise<GeoRecordCategory[]> => {
        const res = await axios.post(`${apiUrl}/geo-record-category`, category);
        return res.data;
    };

    const duplicateGeoRecordCategories = async (
        fromOrgId: number,
        toOrgIds: number[],
        filteredTree: Array<any>,
    ): Promise<any> => {
        const res = await axios.post(
            `${apiUrl}/geo-record-category/duplicate`,
            { fromOrgId, toOrgIds, filteredTree },
        );
        return res.status == 200 || res.status == 201;
    };

    const updateGeoRecordCategory = async (
        category: GeoRecordCategory,
    ): Promise<GeoRecordCategory[]> => {
        const res = await axios.put(`${apiUrl}/geo-record-category`, category);
        return res.data;
    };

    const linkGeoRecordCategoryRole = async (payload: any) => {
        const res = await axios.post(
            `${apiUrl}/geo-record-category/link/role`,
            payload,
        );
        if (res.status == 200) {
            return true;
        }
        return false;
    };

    const deleteGeoRecordCategory = async (id: number) => {
        return await axios.deleteId(`${apiUrl}/geo-record-category/${id}`);
    };

    const getGeoRecordCategorySummaryByRole = async (): Promise<
        GeoRecordCategorySummary[]
    > => {
        const res = await axios.get(
            `${apiUrl}/geo-record-category/summary/role`,
        );
        return res.data;
    };

    const getGeoRecordCategoryByRole = async (): Promise<
        GeoRecordCategorySummary[]
    > => {
        const res = await axios.get(`${apiUrl}/geo-record-category/role`);
        return res.data;
    };

    const getGeoRecordCategoriesSummary = async (): Promise<
        GeoRecordCategorySummary[]
    > => {
        const res = await axios.get(`${apiUrl}/geo-record-category/summary`);
        return res.data;
    };

    const getGeoRecordCategory = async (
        id: number,
    ): Promise<GeoRecordCategory> => {
        const res = await axios.get(`${apiUrl}/geo-record-category/${id}`);
        return res.data;
    };

    const getGeoRecordCategoryById = async (
        id: number,
    ): Promise<GeoRecordCategory> => {
        const res = await axios.get(`${apiUrl}/geo-record-category/${id}`);
        return res.data;
    };

    const categoriesToArray = (cats: any): any[] => {
        let array: any[] = [];

        cats.forEach((cat) => {
            if (cat.children.length === 0) {
                array.push(cat);
            } else {
                array.push(cat);
                array.push(...categoriesToArray(cat.children));
            }
        });

        return array;
    };

    const getAllIconFiles = async () => {
        const res = await axios.get(`${apiUrl}/geo-record-category/all/icons`);
        return res.data;
    };

    const uploadIcon = async (images: any): Promise<any> => {
        const res = await axios.post(
            `${apiUrl}/geo-record-category/icon-file`,
            images,
        );
        return res;
    };

    return {
        geoRecordCategories,
        getAllGeoRecordCategoryAsTree,
        getAllGeoRecordCategoryAsTreeByOrg,
        getGeoRecordCategorySummaryByRole,
        getGeoRecordCategoriesSummary,
        getGeoRecordCategory,
        deleteGeoRecordCategory,
        updateGeoRecordCategory,
        duplicateGeoRecordCategories,
        categoriesToArray,
        getGeoRecordCategoryById,
        getAllGeoRecordCategory,
        getAllGeoRecordCategoryByOrg,
        createGeoRecordCategory,
        getAllIconFiles,
        uploadIcon,
        getGeoRecordCategoryByRole,
        getAllGeoRecordCategoryAsTreeByRole,
        linkGeoRecordCategoryRole,
    };
};
